import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import moment from 'moment';
import { handleStatus, handleStatusColor } from '../../../../shared/utils';
import { IResponseDepositAcceptance } from '../../../../shared/services/types/response';
import { useState } from 'react';
import Loading from '../../../../components/loading';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

interface ModalDepositAcceptanceProps {
    open: boolean;
    deposit: IResponseDepositAcceptance | null;
    onClose: () => void;
    onAccept: () => Promise<void>;
    onRefuse: (reason?: string) => Promise<void>;
    isLoading?: boolean;
}

const ModalDepositAcceptance = ({
    open,
    deposit,
    onClose,
    onAccept,
    onRefuse,
    isLoading = false,
}: ModalDepositAcceptanceProps) => {
    const [refusalReason, setRefusalReason] = useState('');
    const [isRefusingWithReason, setIsRefusingWithReason] = useState(false);

    const handleConfirmAccept = async () => {
        await onAccept();
    };

    const handleConfirmRefuse = async () => {
        if (isRefusingWithReason && !refusalReason.trim()) {
            alert('Por favor, forneça uma razão para a recusa');
            return;
        }
        await onRefuse(isRefusingWithReason ? refusalReason : undefined);
        setRefusalReason('');
        setIsRefusingWithReason(false);
    };

    const handleClose = () => {
        setRefusalReason('');
        setIsRefusingWithReason(false);
        onClose();
    };

    if (!deposit) return null;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Aceite de Depósito - {deposit.number_control_ccs}
            </DialogTitle>
            <DialogContent>
                <Loading isLoading={isLoading} />
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                            Número de Controle CCS
                        </Typography>
                        <Typography variant="body1">{deposit.number_control_ccs}</Typography>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                            Número da Conta de Depósito
                        </Typography>
                        <Typography variant="body1">{deposit.account_deposit_number}</Typography>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                            Data de Criação
                        </Typography>
                        <Typography variant="body1">
                            {moment(deposit.created_at).format('DD/MM/yyyy HH:mm')}
                        </Typography>
                    </Box>

                    {deposit.account_groups && deposit.account_groups.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" color="textSecondary">
                                Grupos de Conta
                            </Typography>
                            <Typography variant="body1">
                                {deposit.account_groups.length} grupo(s)
                            </Typography>
                        </Box>
                    )}

                    <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                            Status Atual
                        </Typography>
                        <Box
                            sx={{
                                display: 'inline-block',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 2,
                                bgcolor: handleStatusColor(deposit.status),
                                color: 'white',
                            }}
                        >
                            <Typography variant="body2">
                                {handleStatus(deposit.status)}
                            </Typography>
                        </Box>
                    </Box>

                    {isRefusingWithReason && (
                        <TextField
                            label="Motivo da Recusa"
                            multiline
                            rows={3}
                            fullWidth
                            value={refusalReason}
                            onChange={(e) => setRefusalReason(e.target.value)}
                            placeholder="Digite o motivo da recusa (opcional)"
                        />
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ gap: 1 }}>
                {!isRefusingWithReason ? (
                    <>
                        <Button onClick={handleClose} disabled={isLoading}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={() => setIsRefusingWithReason(true)}
                            color="error"
                            variant="outlined"
                            disabled={isLoading}
                            startIcon={<CancelIcon />}
                        >
                            Recusar
                        </Button>
                        <Button
                            onClick={handleConfirmAccept}
                            color="success"
                            variant="contained"
                            disabled={isLoading}
                            startIcon={<CheckCircleIcon />}
                        >
                            Aceitar
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            onClick={() => setIsRefusingWithReason(false)}
                            disabled={isLoading}
                        >
                            Voltar
                        </Button>
                        <Button
                            onClick={handleConfirmRefuse}
                            color="error"
                            variant="contained"
                            disabled={isLoading}
                            startIcon={<CancelIcon />}
                        >
                            Confirmar Recusa
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ModalDepositAcceptance;
